import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { NumericFormat } from 'react-number-format'
import { useLocalStorage } from '../../../../../remote/local-storage'
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogTrigger } from '../../../../components/ui/alert-dialog'
import { Button } from '../../../../components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../../components/ui/form'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../../../../components/ui/select'
import { cashRegisterSchema, openCashRegisterForm } from '../../../../validators/cash-register-validator'
import { CashRegisterOpenClose } from '../actions/cash-register-open-close'
import { LoadUsersCashRegister } from '../actions/load-users-cash-register'

interface OpenCashRegisterProps {
  actionReload: () => void
}

export function OpenCashRegister({ actionReload }: OpenCashRegisterProps) {
  const { data: usersCashRegister } = LoadUsersCashRegister()
  const { openCashRegister } = CashRegisterOpenClose()
  const { getProfiles, getId, getName } = useLocalStorage()

  const isProfiileAdmin = getProfiles().includes('ADMIN')

  const [modal, setModal] = useState(false)
  const openModal = () => setModal(true)
  const closeModal = () => {
    setModal(false)
    form.reset()
  }

  const form = useForm<openCashRegisterForm>({
    resolver: zodResolver(cashRegisterSchema),
    defaultValues: {}
  })

  const onSubmit = async (data: any) => {
    try {
      await openCashRegister(data.valorInicial, getId(), data.nomeResponsavel)
      form.reset()
      actionReload()
      closeModal()
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (!isProfiileAdmin) {
      form.setValue('nomeResponsavel', getName())
      form.setValue('idResponsavel', getId())
    }
  }, [usersCashRegister])

  useEffect(() => {
    console.log(form.getValues('valorInicial'))
  }, [form.watch('valorInicial')])

  return (
    <AlertDialog open={modal}>
      <AlertDialogTrigger asChild onClick={openModal}>
        <Button className="text-muted bg-acelerafit-green-400 hover:bg-acelerafit-green-200 flex gap-2">Abrir caixa</Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="min-w-[600px]">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="w-full flex flex-col m-auto gap-4">
            <h3 className="text-3xl text-acelerait-black">Abrir caixa</h3>

            <FormField
              control={form.control}
              name="idResponsavel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400">*</span> Funcionário
                  </FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={(data) => {
                        const userCashRegister = usersCashRegister.find((item: any) => item.id === parseInt(data))
                        form.setValue('nomeResponsavel', userCashRegister.nome)
                        field.onChange(data)
                      }}
                      value={field.value?.toString() || ''}
                      disabled={!isProfiileAdmin}
                    >
                      <SelectTrigger className="">
                        <SelectValue placeholder="Selecione" className="w-full" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>Caixa</SelectLabel>
                          {usersCashRegister &&
                            usersCashRegister.map((item: any) => {
                              return (
                                <SelectItem value={item.id.toString()} className="" key={item.id}>
                                  <div className="flex gap-2">{item.nome}</div>
                                </SelectItem>
                              )
                            })}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors.nomeResponsavel?.message}</FormMessage>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="valorInicial"
              render={({ field: { ref, ...field } }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400">*</span> Valor inicial
                  </FormLabel>
                  <FormControl>
                    <NumericFormat
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      thousandSeparator="."
                      decimalSeparator=","
                      prefix="R$ "
                      decimalScale={2}
                      getInputRef={ref}
                      {...field}
                      fixedDecimalScale={true}
                      allowNegative={false}
                      allowLeadingZeros={false}
                    />
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors.nomeResponsavel?.message}</FormMessage>
                </FormItem>
              )}
            />

            <AlertDialogFooter className="grid grid-cols-[1fr_80px] items-center justify-center">
              <Button type="submit" className="bg-acelerafit-green-200 hover:bg-acelerafit-green-50 text-muted ">
                Enviar
              </Button>
              <AlertDialogCancel onClick={closeModal}>Fechar</AlertDialogCancel>
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  )
}
